<template>
  <tile class="opc-zpa">
    <foldable v-model="isExpanded" :title="title">
      <template #head>
        <CardHeader :logo="logo" :title="title">
          <template #head-right-content>
            <!-- Teleport target container -->
            <TeleportTarget
              :id="planQuantityTeleportId"
              :visible="showCustomCollapseContent"
              ref="teleportTarget"
            />
          </template>
          <div v-if="!isExpanded" class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
            <ul>
              <li v-if="selectZPA">
                {{ getPlanCollapseText(selectZPA) }}
              </li>
            </ul>
          </div>
        </CardHeader>
      </template>
      <div class="divide-line"></div>
      <div class="opc-zpa__main">
        <zm-form>
          <p class="opc-zpa__desc">{{ $t('billing.pbx.plan_desc.ZPA') }}</p>
          <zm-form-item>
            <div class="input-content horizontal-form-item">
              <InputNumberWithPopover v-model="form.number" :min="minHost" :max="maxHost" :increaseTips="maxTips" :decrease-tips="decreaseTips"
                @change="handleNumberChange" size="lg" :disabled="disabled" controls-position="both" label="Zoom Phone Power Pack Select the number of users"
                :teleport-target="planQuantityTeleportTarget" :data-tracking="planDataTracking"
                :data-testid="`checkout-input-license-${defaultZPA ? defaultZPA.plan_code : ''}`"
              />
              <template>
                <span v-if="is_desktop_reactive || is_tablet_reactive" class="opc-zpa__unit">
                  {{ getPlanUnitByQuantity(form.number) }}
                  <span v-html="'\u00d7'"></span>
                  {{$money(amount) }}{{ unit }}<priceVATTip />
                </span>
                <div v-else class="opc-zpa__unit">
                  <p class="opc-zpa__unit--license">{{ getPlanUnitByQuantity(form.number) }}</p>
                  <span class="opc-zpa__unit--price-unit">{{ $money(amount) }}{{ unit }}</span>
                  <priceVATTip />
                </div>
              </template>
            </div>
            <zm-banner v-if="maxBanner" type="warning" class="max-host-banner" >
              <p v-html="maxBanner"></p>
            </zm-banner>
          </zm-form-item>
        </zm-form>
        <div class="opc-zm-base-card__main__footer">
          <div class='opc-zm-base-card__btn--wrap'>
            <zm-popover :disabled='!disableDeleteTips' focus-trap placement='top' tabindex="-1" trigger="hover">
              <template #content>
                <div v-html='disableDeleteTips'></div>
              </template>
              <zm-button v-if="couldBeRemoved" :loading='planCardState.removeLoading' data-testid="checkout-button-240" :disabled='disabled || disableDeleteTips' type='text' @click='removePlanByKey'
                          :aria-label="'remove ' + title + ' plan'">{{ $t('billing.common.remove') }}
              </zm-button>
            </zm-popover>
          </div>
        </div>
      </div>
    </foldable>
  </tile>
</template>
<script>
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import CardHeader from '@/components/plan-card-header.vue';
import { getPlanLogo } from '@/common/plan-logo-map';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { computedCallingPlans } from '@/common/service/computers/plans/pbx';
import { BILLING_CYCLE, contactUsPath, DEFAULT_MAX_HOST, planTypes } from '@/common/constants';
import plansMixin from '@/plans/mx/plan';
import { flowTypes } from '@/common/service/page-config';
import { get } from 'lodash';
import { getPlanCollapseText } from '@/common/product';
import { getPlanUnitByQuantity, planUnitMap } from '@/common/product-unit';
import softBundlePlansMixin from '@/views/config/soft-bundle/mixin/soft-bundle-plan';
import teleportTargetMixin from '@/components/teleport-target/mixin';
import { isZPALinkInZoomPhoneContext } from '@/flows/services/utils';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';

export default {
  data() {
    return {
      logo: getPlanLogo(planTypes.ZOOM_PHONE$ZPA),
      title: this.$t('billing.pbx.plan_name.ZPA'),
      isExpanded: true,
      form: {
        number: 0,
      },
      bd_planType: planTypes.ZOOM_PHONE$ZPA,
    };
  },
  mixins: [plansMixin, softBundlePlansMixin, teleportTargetMixin],
  components: {
    Foldable,
    Tile,
    InputNumberWithPopover,
    CardHeader,
  },
  created() {
    this.initConfigCart();
  },
  computed: {
    ...mapState({
      planMapFlat: (state) => state.ShoppingCart.planMapFlat,
    }),
    ...mapGetters({
      'getSelectedPlans': 'getSelectedPlans',
      'getUserCurrentSubscriptions': 'getUserCurrentSubscriptions',
      'plancodeMapSubscriptionDisabled': 'plancodeMapSubscriptionDisabled',
      'boughtPlans': 'boughtPlans',
      'basicConfig': 'basicConfig',
      'isZPAContext': 'isZPAContext',
    }),
    planDataTracking() {
      return {
        buyInteraction: 'number of licenses',
        planName: getTrackingProductNameByPlanType(planTypes.ZOOM_PHONE$ZPA),
      }
    },
    couldBeRemoved() {
      if (this.soft_bundle_hiddenRemoveBtn) return false;
      if (isZPALinkInZoomPhoneContext()) return false;
      return true;
    },
    disabled() {
      const planCode = this.selectZPA?.plan_code;
      return !!this.plancodeMapSubscriptionDisabled[planCode];
    },
    allPlans() {
      return this.planMapFlat.filter(item => item.type === planTypes.ZOOM_PHONE$ZPA);
    },
    isStandardPrice() {
      const isEditFlow = this.$context.flow === flowTypes.edit;
      return (isEditFlow && !this.hasAccountDiscount) ? this.boughtZPA?.isStandardPrice : true;
    },
    minHost() {
      if (this.$context.flow === flowTypes.edit && (this.basicConfig.downSellData.disableDownSell || !this.isStandardPrice)) {
        return get(this.boughtZPA, 'cartInfo.selected_number', 1);
      }
      return 1;
    },
    maxHost() {
      if (this.canAutoAddCallingPlan) return DEFAULT_MAX_HOST;
      if (this.$context.is.edit) return this.allPlans[0]?.max || DEFAULT_MAX_HOST;
      if (this.isZPAContext) {
        const boughtCallingPlanCount = computedCallingPlans(this.boughtPlans).reduce((total, x) => total + (x.cartInfo.selected_number ?? 0), 0);
        return boughtCallingPlanCount || 1;
      }
      const selectCallingPlanCount = computedCallingPlans(this.getSelectedPlans).reduce((total, x) => total + (x.cartInfo.selected_number ?? 0), 0);
      return selectCallingPlanCount || 1;
    },
    maxTips() {
      return this.form.number >= this.maxHost ? this.$t('billing.pbx.zpa_cant_exceed_calling') : '';
    },
    maxBanner() {
      if (!this.isZPAContext) return;
      return this.form.number >= this.maxHost ? this.$t('billing.custom_avatars.addon_user_limit_tips', [
        this.$t('billing.pbx.plan_name.ZPA'),
        this.$t('billing.planform.title.zoomphone'),
      ]) : '';
    },
    decreaseTips() {
      if (this.selectZPA?.cartInfo?.selected_number <= this.boughtZPA?.cartInfo?.selected_number) {
        if (this.basicConfig.downSellData.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }

        return this.forNonStandardPriceTips;
      }

      return '';
    },
    currentBillingCycle() {
      const selectCallingPlan = computedCallingPlans(this.getUserCurrentSubscriptions)[0];
      if (selectCallingPlan) {
        return selectCallingPlan.billing_cycle;
      }
      return BILLING_CYCLE.ANNUAL;
    },
    cycleMap() {
      return {
        [BILLING_CYCLE.MONTHLY]: this.allPlans.find(item => item.billing_cycle === BILLING_CYCLE.MONTHLY),
        [BILLING_CYCLE.ANNUAL]: this.allPlans.find(item => item.billing_cycle === BILLING_CYCLE.ANNUAL),
      }
    },
    boughtZPA() {
      return this.boughtPlans.find((p) => p.plan_code === this.selectZPA?.plan_code);
    },
    selectZPA() {
      return this.getSelectedPlans.find(item => item.type === planTypes.ZOOM_PHONE$ZPA);
    },
    defaultZPA() {
      if (this.selectZPA) {
        return this.selectZPA;
      }
      return this.allPlans.find(item => item.billing_cycle === this.currentBillingCycle) || this.allPlans[0];
    },
    amount() {
      if (!this.defaultZPA) {
        return 0;
      }
      return this.defaultZPA.amount;
    },
    unit() {
      return this.$t(`billing.common.per_billing_cycle.${this.defaultZPA.billing_cycle}`)
    },
    disableDeleteTips() {
      const hasBought = this.boughtPlans.find((p) => p.plan_code === this.selectZPA?.plan_code);
      if ((hasBought && this.basicConfig?.isDirectSales) || hasBought?.isDisablePlanChangeOnline || hasBought?.ratePlans?.length > 1) {
        return this.$t('billing.cancel_disable_contact_us', {
          url: contactUsPath,
        })
      }
      return null;
    },
    /** soft bundle custom collapse content start */
    showCustomCollapseContent() {
      const onlyHaveOneZPAPlan = this.getSelectedPlans?.filter(plan => plan.type === planTypes.ZOOM_PHONE$ZPA)?.length === 1;
      return this.soft_bundle_showCustomCollapseContent && !this.isExpanded && onlyHaveOneZPAPlan;
    },
    /** soft bundle custom collapse content end */
    canAutoAddCallingPlan() {
      return (
        isZPALinkInZoomPhoneContext() &&
        computedCallingPlans(this.getSelectedPlans).length === 1
      );
    },
  },
  methods: {
    ...mapActions([ 'switchSelectPlan']),
    ...mapMutations({
      updateSelectPlan: 'updateSelectPlan',
    }),
    initConfigCart() {
      if (this.selectZPA) {
        this.form.number = this.selectZPA.cartInfo.selected_number;
        return;
      }
      this.form.number = 1;
      this.handleNumberChange(1);
    },
    handleNumberChange(number, oldValue) {
      if (oldValue && oldValue > number) {
        this.$tracking.buyInteraction('zoom phone add-on', `Zoom Phone Power Pack - decrease - ${number}`);
      } else if (oldValue && oldValue < number) {
        this.$tracking.buyInteraction('zoom phone add-on', `Zoom Phone Power Pack - increase - ${number}`);
      }
      if (!this.defaultZPA) return;
      const oldLicense = this.defaultZPA.cartInfo.selected_number;
      this.updateSelectPlan({
        plan_code: this.defaultZPA.plan_code,
        cartInfo: { selected_number: number },
      });
      this.$context.$emit('power-pack-license-change', { newLicense: number, oldLicense });
    },
    switchBillingCycle() {
      if (!this.selectZPA) {
        this.initConfigCart()
      }
      const nextZPA = this.cycleMap[this.currentBillingCycle];
      if (!nextZPA) return;
      if (this.selectZPA.plan_code === nextZPA.plan_code) return;
      this.switchSelectPlan([this.selectZPA.plan_code, nextZPA.plan_code, this.selectZPA.cartInfo])
    },
    getPlanUnitByQuantity(quantity) {
      return getPlanUnitByQuantity(planUnitMap.user, quantity);
    },
    getPlanCollapseText(plan) {
      const { amount, billing_cycle, cartInfo, cycleValue } = plan;
      return getPlanCollapseText({
        amount,
        billingCycle: billing_cycle,
        quantity: cartInfo.selected_number,
        cycleValue,
        unit: planUnitMap.user,
      });
    },
  },
  watch: {
    isExpanded: {
      handler(val) {
        this.$tracking.buyInteraction(
          'product details accordion',
          `Zoom Phone Power Pack  - ${val ? 'expand' : 'collapse'}`,
        );
      },
    },
    currentBillingCycle(v, ov) {
      if (v !== ov) {
        this.switchBillingCycle();
      }
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/zoom-card.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-zpa {
  .opc-zm-base-card {
    @include media(mobile) {
      &__name-collapse {
        display: block;
        padding-right: 70px;
      }
    }
    &__name {
      font-style: normal;
      font-weight: 600;
      font-size: 20px;
      line-height: 120%;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      display: flex;
      flex-direction: row;
      align-items: center;
    }
  }

  &__main {
    margin-top: 0;
    border-top: none;
    .zbo-form-item.horizontal-form-item {
      .zbo-form-item__widgets {
        display: flex;
        align-items: center;
      }
    }
    .zbo-form-item__label-wrapper {
      display: none;
    }
  }

  &__unit {
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
  }

  &__desc {
    font-style: normal;
    font-weight: 450;
    font-size: 14px;
    line-height: 150%;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    margin-bottom: 16px;
  }

  @include media(tablet, desktop) {
    &__unit {
      margin-left: 8px;
    }
  }

  @include media(mobile) {
    &__unit {
      margin-left: 8px;

      &--license {
        color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
      }

      &--price-unit {
        color: var(--zoom-color-text-strong-neutral, #555B62);
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
      }
    }
  }
}
.max-host-banner {
  margin-top: 8px;
}
</style>
